import { useEffect, useRef, useState } from 'react';

import { useMotion } from '../hooks/useMotion';
import { scaleDuration, systemPrefersReducedMotion } from '../lib/motion';

/**
 * Count-up number for KPI cards. Animates from the previously shown value to
 * the new one (~600 ms ease-out); quicker on the reduced motion level and
 * instant when motion is switched off (or the system asks for less motion).
 * `format` receives the in-flight value (e.g. to render "12,5 h").
 */
export const AnimatedNumber = ({
  value,
  format,
  durationMs = 600,
}: {
  value: number;
  format?: (value: number) => string;
  durationMs?: number;
}) => {
  const { level } = useMotion();
  const duration = scaleDuration(durationMs, level);
  const [shown, setShown] = useState(value);
  // Tracks what is actually on screen, including cancelled mid-animation
  // values — animating from here prevents freezes/backward jumps when the
  // value flaps during a refetch (A → 0 → A inside the animation window).
  const shownRef = useRef(value);
  const frameRef = useRef<number>(0);

  useEffect(() => {
    if (duration === 0 || systemPrefersReducedMotion() || !Number.isFinite(value)) {
      shownRef.current = value;
      setShown(value);
      return;
    }
    const from = shownRef.current;
    if (from === value) {
      return;
    }
    const startedAt = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const current = t < 1 ? from + (value - from) * eased : value;
      shownRef.current = current;
      setShown(current);
      if (t < 1) {
        frameRef.current = requestAnimationFrame(tick);
      }
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [value, duration]);

  return <span className="tabular-nums">{format ? format(shown) : Math.round(shown)}</span>;
};
